/* eslint-disable react/jsx-no-useless-fragment */
import { SetStateAction, Dispatch, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  SxProps,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Radio,
  RadioGroup,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getUserStorage } from "util/local_storage";
import CustomTextField from "./CustomTextField";
import {
  Category,
  Shop,
  OrderLargeClassification,
  Supplier,
  TyreOptions,
  WheelOptions,
  OrderStatus,
  OrderReceiptStatus,
  HasRepresentativeCd,
  ReceiveMode,
} from "../types";
import {
  orderReceiptStatusList,
  orderStatusOptionList,
  prefectures,
} from "../constants";
import { IncrementalSearchElement, SelectForm } from "./Common";
import Surround from "./Surround";
import { extractNoDupplicationSupplier, getReceivedStatusCandidates, getReceivedStatusParam } from "../utils/common";

const incrementalSearchOptionLength = 10;

type Props = {
  open: boolean;
  onClose: Function;
  onSearch: Function;
  isRepresentativeCd: HasRepresentativeCd;
  isCustomerOrder: boolean | undefined;
  shopName: string;
  orderStatus: OrderStatus;
  orderReceiptStatus: OrderReceiptStatus;
  storeOrderer: string;
  approver: string;
  orderStartDate: string;
  orderEndDate: string;
  companyCd: string;
  companyName: string;
  centerCd: string;
  orderNumber: string;
  productCd: string;
  productName: string;
  brand: string;
  janCd: string;
  productNumber: string;
  tyreWidth: number | undefined;
  tyreRatio: number | undefined;
  tyreRimDiam: number | undefined;
  wheelWidth: number | undefined;
  wheelPcd: number | undefined;
  wheelHole: number | undefined;
  wheelRimDiam: number | undefined;
  classification: string;
  storehouse?: string;
  supplier: string;
  targetShopCdList?: string[];
  targetShopLengthLimit?: number; // 対象店舗の最大選択数
  tyreOptions: TyreOptions;
  wheelOptions: WheelOptions;
  classificationOptions: OrderLargeClassification[];
  brandOptions: string[];
  shopOptions?: Shop[];
  selectedReceivedMode?: ReceiveMode,
  setIsRepresentativeCd: Dispatch<SetStateAction<HasRepresentativeCd>>;
  setIsCustomerOrder: Dispatch<SetStateAction<boolean | undefined>>;
  setShopName: Dispatch<SetStateAction<string>>;
  setOrderStatus: Dispatch<SetStateAction<OrderStatus>>;
  setOrderReceiptStatus: Dispatch<SetStateAction<OrderReceiptStatus>>;
  setStoreOrderer: Dispatch<SetStateAction<string>>;
  setApprover: Dispatch<SetStateAction<string>>;
  setOrderStartDate: Dispatch<SetStateAction<string>>;
  setOrderEndDate: Dispatch<SetStateAction<string>>;
  setCompanyCd: Dispatch<SetStateAction<string>>;
  setCompanyName: Dispatch<SetStateAction<string>>;
  setCenterCd: Dispatch<SetStateAction<string>>;
  setOrderNumber: Dispatch<SetStateAction<string>>;
  setProductCd: Dispatch<SetStateAction<string>>;
  setProductName: Dispatch<SetStateAction<string>>;
  setBrand: Dispatch<SetStateAction<string>>;
  setjanCd: Dispatch<SetStateAction<string>>;
  setProductNumber: Dispatch<SetStateAction<string>>;
  setTyreWidth: Dispatch<SetStateAction<number | undefined>>;
  setTyreRatio: Dispatch<SetStateAction<number | undefined>>;
  setTyreRimDiam: Dispatch<SetStateAction<number | undefined>>;
  setWheelWidth: Dispatch<SetStateAction<number | undefined>>;
  setWheelPcd: Dispatch<SetStateAction<number | undefined>>;
  setWheelHole: Dispatch<SetStateAction<number | undefined>>;
  setWheelRimDiam: Dispatch<SetStateAction<number | undefined>>;
  setClassification: Dispatch<SetStateAction<string>>;
  setCategory: Dispatch<SetStateAction<Category>>;
  setStorehouse?: Dispatch<SetStateAction<string>>;
  setSupplier: Dispatch<SetStateAction<string>>;
  setTargetShopCdList?: Dispatch<SetStateAction<string[]>>;
  screenType:
    | "本部依頼一覧"
    | "本部履歴一覧"
    | "本部作成"
    | "本部編集"
    | "店舗一覧"
    | "店舗作成"
    | "企業一覧"
    | "企業作成"
    | "仕入先・外部倉庫"
    | "一般店"
    | "ジョイフル"
    | "ジョイフル商品";
};

const styles: { [key: string]: SxProps } = {
  searchRowBox: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  searchRowBox3: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    width: "464px",
  },
};

/**
 * 検索コンポーネント
 * @param root0 コンポーネントのprops
 * @returns JSX.Element
 */
export default function OrderSearchDialog({
  open,
  onClose,
  onSearch,
  isRepresentativeCd,
  isCustomerOrder,
  shopName,
  orderStatus,
  orderReceiptStatus,
  storeOrderer,
  approver,
  orderStartDate,
  orderEndDate,
  companyCd,
  companyName,
  centerCd,
  orderNumber,
  productCd,
  productName,
  brand,
  janCd,
  productNumber,
  tyreWidth,
  tyreRatio,
  tyreRimDiam,
  wheelWidth,
  wheelPcd,
  wheelHole,
  wheelRimDiam,
  classification,
  storehouse,
  supplier,
  targetShopCdList,
  targetShopLengthLimit,
  tyreOptions,
  wheelOptions,
  classificationOptions,
  brandOptions,
  shopOptions,
  selectedReceivedMode,
  setIsRepresentativeCd,
  setIsCustomerOrder,
  setShopName,
  setOrderStatus,
  setOrderReceiptStatus,
  setStoreOrderer,
  setApprover,
  setOrderStartDate,
  setOrderEndDate,
  setCompanyCd,
  setCompanyName,
  setCenterCd,
  setOrderNumber,
  setProductCd,
  setProductName,
  setBrand,
  setjanCd,
  setProductNumber,
  setTyreWidth,
  setTyreRatio,
  setTyreRimDiam,
  setWheelWidth,
  setWheelPcd,
  setWheelHole,
  setWheelRimDiam,
  setClassification,
  setCategory,
  setStorehouse,
  setSupplier,
  setTargetShopCdList,
  screenType,
}: Props) {
  const user = getUserStorage();
  const { showAlert } = useContext(SuccessAlertContext);
  const initValue = () => {
    const backup = {
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouse,
      supplier,
      targetShopCdList,
      shopName,
    };
    return backup;
  };
  const [backup, setBackup] = useState(initValue());
  const resetValue = () => {
    setProductCd(backup.productCd);
    setProductName(backup.productName);
    setBrand(backup.brand);
    setjanCd(backup.janCd);
    setProductNumber(backup.productNumber);
    setTyreWidth(backup.tyreWidth);
    setTyreRatio(backup.tyreRatio);
    setTyreRimDiam(backup.tyreRimDiam);
    setWheelWidth(backup.wheelWidth);
    setWheelPcd(backup.wheelPcd);
    setWheelHole(backup.wheelHole);
    setWheelRimDiam(backup.wheelRimDiam);
    setClassification(backup.classification);
    setShopName(backup.shopName);
    if (setStorehouse && backup.storehouse != null) setStorehouse(backup.storehouse);
    if (setSupplier && backup.supplier != null) setSupplier(backup.supplier);
    // 大分類があればカテゴリも存在する
    if (backup.classification)
      setCategory(
        classificationOptions.filter((co) => co.name === backup.classification)[0].category,
      );
    if (setTargetShopCdList && backup.targetShopCdList)
      setTargetShopCdList(backup.targetShopCdList);
  };
  const handleClose = () => {
    resetValue();
    onClose();
  };

  // 大分類がないscreenType
  const checkHasClassScreenType = () =>
    !(screenType === "ジョイフル" || screenType === "仕入先・外部倉庫");

  // 大分類から仕入先を探す
  const calcSupplierOptions = () => (
    // let sOptions: string[] = [];
    // if (classification) {
    //   // 分類の中に仕入先のみ候補にする
    //   const sList = classificationOptions.filter((c) => c.name === classification)[0].suppliers;
    //   sOptions = sList ? sList.map((s) => s.supplierName) : [];
    // } else
    //   sOptions = extractNoDupplicationSupplier(classificationOptions).map((so) => so.supplierName); // 全仕入先を候補にする
    // return sOptions;
    extractNoDupplicationSupplier(classificationOptions).map((so) => so.supplierName) // 全仕入先を候補にする
  );

  // 表示する注文
  const searchHeadElementRow = () => {
    if (screenType === "本部依頼一覧" || screenType === "本部履歴一覧" || screenType === "本部作成") {
      return (
        <Surround
          titleComponent={
            <Box
              position="absolute"
              top={-10}
              left={8}
              px={1}
              sx={{ backgroundColor: "var(--white)" }}
            >
              <Typography fontSize={14} color="secondary.main">
                表示する注文（複数選択可）
              </Typography>
            </Box>
          }
        >
          <Box display="flex" flexDirection="row" justifyContent="space-evenly" width="100%">
            <Box>
              <FormControlLabel
                key="is_customer_order"
                control={
                  <Checkbox
                    color="secondary"
                    checked={isCustomerOrder === true}
                    onChange={(_, check) => setIsCustomerOrder(check ? true : undefined)}
                    inputProps={{ "aria-label": "customer-selected" }}
                  />
                }
                label="客注有りのみ"
              />
            </Box>
            <Box>
              <FormControlLabel
                key="is_customer_order"
                control={
                  <Checkbox
                    color="secondary"
                    checked={isCustomerOrder === false}
                    onChange={(_, check) => setIsCustomerOrder(check ? false : undefined)}
                    inputProps={{ "aria-label": "customer-selected" }}
                  />
                }
                label="客注無しのみ"
              />
            </Box>
            <Box>
              <FormControlLabel
                key="is_representative_cd"
                control={
                  <Checkbox
                    color="secondary"
                    checked={isRepresentativeCd === "代表コード有りのみ"}
                    onChange={(_, check) =>
                      setIsRepresentativeCd(check ? "代表コード有りのみ" : "全て")
                    }
                    inputProps={{ "aria-label": "representative_cd-selected" }}
                  />
                }
                label="代表コード"
              />
            </Box>
          </Box>
        </Surround>
      );
    }
    if (screenType === "店舗作成" || screenType === "企業作成") {
      return (
        <Surround
          titleComponent={
            <Box
              position="absolute"
              top={-10}
              left={8}
              px={1}
              sx={{ backgroundColor: "var(--white)" }}
            >
              <Typography fontSize={14} color="secondary.main">
                表示する注文
              </Typography>
            </Box>
          }
        >
          <FormControl sx={{ width: "100%" }}>
            <RadioGroup value={isRepresentativeCd} onChange={(e) => setIsRepresentativeCd(e.target.value as HasRepresentativeCd)}>
              <Box display="flex" flexDirection="row" justifyContent="space-around" width="100%">
                <FormControlLabel
                  value="全て"
                  control={<Radio />}
                  sx={{ ml: 2, width: "calc(50% - 16px)", height: 42 }}
                  label="全て表示対象"
                />
                <FormControlLabel
                  value="代表コード有りのみ"
                  control={<Radio />}
                  sx={{ ml: 2, width: "calc(50% - 16px)", height: 42 }}
                  label="代表コード有りのみ"
                />
              </Box>
              <Box>
                <FormControlLabel
                  value="代表コード無しのみ"
                  control={<Radio />}
                  sx={{ ml: 2, width: "calc(50% - 16px)", height: 42 }}
                  label="代表コード無しのみ"
                />
              </Box>
            </RadioGroup>
          </FormControl>
        </Surround>
      );
    }
    return null;
  };

  const getOrderStatusOptions = () => {
    if (screenType === "仕入先・外部倉庫" && selectedReceivedMode !== undefined) {
      const candidates = getReceivedStatusCandidates(selectedReceivedMode, true);
      return candidates.map(c => orderReceiptStatusList[c]);
    }
    if (screenType === "本部履歴一覧") return orderStatusOptionList.slice(3,8);
    if (screenType === "本部依頼一覧") return orderStatusOptionList.slice(2,3);
    if (screenType === "本部編集") return orderStatusOptionList.slice(2,3);
    if (screenType === "本部作成") return orderStatusOptionList.slice(1,2); // 本来は8からだが、表示は未発注、確認中
    if (screenType === "店舗一覧") return orderStatusOptionList.slice(2,8);
    if (screenType === "店舗作成") return orderStatusOptionList.slice(1,2);

    return orderStatusOptionList.slice(1);
  }

  const searchElementRow = () => {
    let searchElements = [
      // 0
      <Box key="order_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="orderNumber"
          type="text"
          label="注文番号"
          text={orderNumber}
          setText={setOrderNumber}
          maxLength={100}
          size="small"
          width={218}
          isNumericText
        />
      </Box>,
      // 1
      <Box key="product_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productCd"
          type="text"
          label="商品コード"
          text={productCd}
          setText={setProductCd}
          maxLength={12}
          size="small"
          width={218}
          isNumericText
        />
      </Box>,
      // 2
      <Box key="jan_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="janCd"
          type="text"
          label="JANコード"
          text={janCd}
          setText={setjanCd}
          maxLength={13}
          size="small"
          width={218}
          isNumericText
        />
      </Box>,
      // 3
      <SelectForm
        key="classification"
        id="classification"
        label="大分類"
        optionList={classificationOptions.map((c) => c.name)}
        value={classification}
        maxWidth={218}
        setValue={(val) => {
          if (classificationOptions.find((co) => co.name === val)) {
            setCategory(classificationOptions.filter((co) => co.name === val)[0].category);
            // const sups = classificationOptions.filter((c) => c.name === val)[0].suppliers;
            // if (supplier && setSupplier && sups && !sups.some((s) => s.supplierName === supplier)) {
            //   setSupplier("");
            // }
          }
          setClassification(val);
        }}
        isContainBlank
        // required
      />,
      // 4
      <IncrementalSearchElement
        key="supplier_name"
        label="仕入先名"
        options={calcSupplierOptions()}
        value={supplier}
        onChange={(val: string) => setSupplier(val)}
        optionLength={incrementalSearchOptionLength}
        isBlankNoOptions
      />,
      // 5
      <IncrementalSearchElement
        key="brand"
        label="ブランド"
        options={brandOptions}
        value={brand}
        onChange={(val: string) => setBrand(val)}
        optionLength={incrementalSearchOptionLength}
      />,
      // 6
      <Box key="product_name" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productName"
          type="text"
          label="商品名"
          text={productName}
          setText={setProductName}
          maxLength={100}
          size="small"
          width={218}
        />
      </Box>,
      // 7
      <Box key="product_number" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="productNumber"
          type="text"
          label="品番"
          text={productNumber}
          setText={setProductNumber}
          maxLength={100}
          size="small"
          width={218}
        />
      </Box>,
      // 8
      <SelectForm
        key="order_status"
        id="orderStatus"
        label="ステータス"
        maxWidth={218}
        optionList={
          getOrderStatusOptions()
        }
        value={screenType === "仕入先・外部倉庫" ? orderReceiptStatus : orderStatus}
        setValue={(val) => {
          if (screenType === "仕入先・外部倉庫") setOrderReceiptStatus(val as OrderReceiptStatus);
          else setOrderStatus(val as OrderStatus);
        }}
        isContainBlank
      />,
      // 9
      <Box key="shop_name" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="shopName"
          type="text"
          label="店舗名"
          text={shopName}
          setText={setShopName}
          maxLength={100}
          size="small"
          width={218}
        />
      </Box>,
      // 10
      <Box key="store_orderer" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="storeOrderer"
          type="text"
          label="店舗発注者"
          text={storeOrderer}
          setText={setStoreOrderer}
          maxLength={100}
          size="small"
          width={218}
        />
      </Box>,
      // 11
      <Box key="approver" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="approver"
          type="text"
          label="承認者"
          text={approver}
          setText={setApprover}
          maxLength={100}
          size="small"
          width={218}
        />
      </Box>,
      // 12
      <FormControlLabel
        key="is_representative_cd"
        sx={{ width: "calc(50% - 16px)" }}
        control={
          <Checkbox
            color="secondary"
            checked={isRepresentativeCd === "代表コード有りのみ"}
            onChange={(_, check) =>
              setIsRepresentativeCd(check ? "代表コード有りのみ" : "全て")
            }
            inputProps={{ "aria-label": "representative_cd-selected" }}
          />
        }
        label="代表コード"
      />,
      // 13
      <Box key="company_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="companyCd"
          type="text"
          label="企業コード"
          text={companyCd}
          setText={setCompanyCd}
          maxLength={6}
          size="small"
          isNumericText
          width={218}
        />
      </Box>,
      // 14
      <Box key="company_name" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="companyName"
          type="text"
          label="企業名"
          text={companyName}
          setText={setCompanyName}
          maxLength={100}
          size="small"
          width={218}
        />
      </Box>,
      // 15
      <Box key="center_cd" m={1} width="calc(50% - 16px)">
        <CustomTextField
          id="centerCd"
          type="text"
          label="センターコード"
          text={centerCd}
          setText={setCenterCd}
          maxLength={50}
          size="small"
          width={218}
        />
      </Box>,
    ];

    // 画面の種類によって使う項目に絞る
    let supplierSearchElementIndices: number[] = [];
    switch (screenType) {
      case "本部依頼一覧":
      case "本部履歴一覧":
      case "本部編集":
      case "本部作成":
        supplierSearchElementIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
        break;
      case "店舗一覧":
        supplierSearchElementIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12];
        break;
      case "店舗作成":
        supplierSearchElementIndices = [1, 2, 3, 4, 5, 6, 7, 10, 11];
        break;
      case "一般店":
        supplierSearchElementIndices = [1, 2, 3, 4, 5, 6, 7];
        break;
      case "ジョイフル":
        supplierSearchElementIndices = [13, 14, 15];
        break;
      case "ジョイフル商品":
        supplierSearchElementIndices = [1, 2, 3, 4, 5, 6];
        break;
      case "仕入先・外部倉庫":
        supplierSearchElementIndices = [0, 1, 2, 6, 7, 8, 9];
        break;
      case "企業一覧":
        supplierSearchElementIndices = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        break;
      case "企業作成":
        supplierSearchElementIndices = [1, 2, 3, 4, 5, 6, 7, 9, 10, 11];
        break;
      default:
        break;
    }
    searchElements = searchElements.filter((_, index) =>
      supplierSearchElementIndices.includes(index),
    );

    const len = Math.ceil(searchElements.length / 2);
    const couples: JSX.Element[] = [];
    for (let i = 0; i < len; i += 1) {
      const element1 = searchElements[i * 2];
      let element2: JSX.Element | undefined;
      if (i * 2 + 1 < searchElements.length) {
        element2 = searchElements[i * 2 + 1];
      }
      couples.push(
        <Box sx={styles.searchRowBox} key={`fieldCouple${i + 1}`}>
          {element1}
          {element2 ?? <></>}
        </Box>,
      );
    }
    return <>{couples}</>;
  };

  // 注文日の枠
  const searchDateElementRow = () => (
    <Surround title="注文日"
      // description="※5年前までの注文が検索可能です。" 1か月ほどで削除する
    >
      <Box marginTop={0.5} />
      <Box sx={styles.searchRowBox3}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
          <DatePicker
            label="注文日（開始）"
            format="YYYY/MM/DD"
            maxDate={orderEndDate ? dayjs(orderEndDate) : dayjs().add(1, "day")}
            value={orderStartDate ? dayjs(orderStartDate, "YYYY/MM/DD") : null}
            onChange={(val) => setOrderStartDate(val ? val.format("YYYY/MM/DD") : "")}
            slotProps={{ textField: { size: "small" } }}
            sx={{
              width: "calc(50% - 16px)",
              margin: "8px",
            }}
            // shouldDisableDate={(date) => {
            //   if (date.day() === 6 || date.day() === 0 || isHoliday(date.toDate())) {
            //     return true;
            //   }
            //   return false;
            // }}
          />
        </LocalizationProvider>
        <Box width="16px" textAlign="center" lineHeight="54px">
          ～
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
          <DatePicker
            label="注文日（終了）"
            format="YYYY/MM/DD"
            maxDate={dayjs().add(1, "day")}
            minDate={orderStartDate ? dayjs(orderStartDate) : undefined}
            value={orderEndDate ? dayjs(orderEndDate, "YYYY/MM/DD") : null}
            onChange={(val) => setOrderEndDate(val ? val.format("YYYY/MM/DD") : "")}
            slotProps={{ textField: { size: "small" } }}
            sx={{
              width: "calc(50% - 16px)",
              margin: "8px",
            }}
            // shouldDisableDate={(date) => {
            //   if (date.day() === 6 || date.day() === 0 || isHoliday(date.toDate())) {
            //     return true;
            //   }
            //   return false;
            // }}
          />
        </LocalizationProvider>
      </Box>
      <Box marginTop={1} />
    </Surround>
  );

  const [selectedPrefectureIndex, setSelectedPrefectureIndex] = useState(0);
  // チェックボックス押下時の処理
  const handleCheck = (
    shopCdList: string[],
    checked: boolean,
    sTargetShopCdList: Dispatch<SetStateAction<string[]>>,
    tarShopLengthLimit: number,
    tarShopCdList: string[],
    sOptions: Shop[],
  ) => {
    sTargetShopCdList((prev) => {
      if (prev.length >= tarShopLengthLimit && checked) {
        showAlert(`対象店舗は最大${targetShopLengthLimit}店舗まで選択可能です。`, "error");
        return prev;
      }
      // 全選択の場合、上限値までのみチェックする
      const shopCds = checked
        ? (() => {
            // 現在選択済みの店舗を候補から除去
            const sCds = shopCdList.filter((sCd) => !prev.includes(sCd));
            // 残りの数だけ選択する
            return sCds.slice(0, tarShopLengthLimit - tarShopCdList.length);
          })()
        : shopCdList;

      let newList = [...prev];
      shopCds.forEach((shopCd) => {
        if (checked) newList.push(shopCd);
        else newList = newList.filter((sCd) => sCd !== shopCd);
      });
      // 枠内全選択の場合に重複を除去
      const noDuplicateList = Array.from(new Set(newList));
      // shopOptions順にソートする
      const sortedList: string[] = [];
      sOptions.forEach((so) => {
        if (noDuplicateList.includes(so.shopCd)) sortedList.push(so.shopCd);
      });
      return sortedList;
    });
  };
  const searchCheckboxElement = () => {
    const checkboxElements: JSX.Element[] = [];

    // 企業用で使用
    if (
      screenType === "企業一覧" &&
      shopOptions &&
      shopOptions.length > 0 &&
      setTargetShopCdList &&
      targetShopCdList &&
      targetShopLengthLimit
    ) {
      const targetShopOptions =
        selectedPrefectureIndex === 0
          ? shopOptions
          : shopOptions.filter((so) => so.prefecture === selectedPrefectureIndex);

      // 上限値選択されているか、絞り込んだチェックボックスが全て選択されているか
      const checkCheckedTargetShopOptionsAll = () =>
        targetShopCdList.length >= targetShopLengthLimit ||
        targetShopOptions.length ===
          targetShopOptions.filter((tso) => targetShopCdList.includes(tso.shopCd)).length;

      checkboxElements.push(
        <Box mt={2}>
          <Surround
            titleComponent={
              <Box
                position="absolute"
                top={-15}
                left={8}
                px={1}
                sx={{ backgroundColor: "var(--white)" }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color="secondary"
                      checked={checkCheckedTargetShopOptionsAll()}
                      onChange={(event, checked) =>
                        handleCheck(
                          Array.from(
                            { length: targetShopOptions.length },
                            (_, index) => targetShopOptions[index].shopCd,
                          ),
                          checked,
                          setTargetShopCdList,
                          targetShopLengthLimit,
                          targetShopCdList,
                          shopOptions,
                        )
                      }
                      inputProps={{
                        "aria-label": "all-selected",
                      }}
                      sx={{ py: 0, pr: 0.5 }}
                    />
                  }
                  label={
                    !checkCheckedTargetShopOptionsAll()
                      ? `枠内${Math.min(targetShopLengthLimit - targetShopCdList.length, targetShopOptions.filter((tso) => !targetShopCdList.includes(tso.shopCd)).length)}件まとめて選択`
                      : "枠内全解除"
                  }
                  sx={{ ".MuiFormControlLabel-label": { fontSize: 14 } }}
                />
              </Box>
            }
          >
            <Box px={2} py={1}>
              {targetShopOptions.map((so) => {
                const checked = targetShopCdList.includes(so.shopCd);
                return (
                  <FormControlLabel
                    key={so.shopCd}
                    control={
                      <Checkbox
                        color="secondary"
                        checked={checked}
                        onChange={(_, check) =>
                          handleCheck(
                            [so.shopCd],
                            check,
                            setTargetShopCdList,
                            targetShopLengthLimit,
                            targetShopCdList,
                            shopOptions,
                          )
                        }
                        inputProps={{
                          "aria-label": "all-selected",
                        }}
                      />
                    }
                    label={so.shopName}
                  />
                );
              })}
            </Box>
          </Surround>
        </Box>,
      );

      checkboxElements.push(
        <Box mt={2} ml={3} minWidth={250}>
          <Surround
            titleComponent={
              <Box
                position="absolute"
                top={-14}
                left={8}
                px={1}
                sx={{ backgroundColor: "var(--white)" }}
              >
                <Box component="span" fontSize={14} mr={1}>
                  選択中店舗
                </Box>
                <Button
                  variant="custom-outlined"
                  sx={{ borderRadius: 2, m: 0, px: 1, py: 0, fontSize: 14 }}
                  onClick={() => setTargetShopCdList([])}
                >
                  全店舗クリア
                </Button>
              </Box>
            }
          >
            <Box display="flex" flexDirection="row" flexWrap="wrap" mt={2}>
              {targetShopCdList.length > 0 ? (
                targetShopCdList.map((tsCd) => {
                  const sName = shopOptions.filter((s) => s.shopCd === tsCd)[0].shopName;
                  return (
                    <Button
                      key={tsCd}
                      variant="custom-outlined"
                      sx={{ borderRadius: 2, m: 1, px: 1, py: 0, fontSize: 14 }}
                      onClick={() =>
                        handleCheck(
                          [tsCd],
                          false,
                          setTargetShopCdList,
                          targetShopLengthLimit,
                          targetShopCdList,
                          shopOptions,
                        )
                      }
                    >
                      {sName}
                      <Box>×</Box>
                    </Button>
                  );
                })
              ) : (
                <Box m={1.2}>選択なし</Box>
              )}
            </Box>
          </Surround>
        </Box>,
      );
    }
    return (
      <Box mt={2}>
        <Surround title="対象店舗">
          <Box p={1.5}>
            <SelectForm
              id="search-shop-by-prefecture"
              label="都道府県を選択"
              optionList={
                shopOptions
                  ? [
                      prefectures[0],
                      ...prefectures.filter((_, i) =>
                        shopOptions.some((so) => so.prefecture === i),
                      ),
                    ]
                  : prefectures
              }
              value={prefectures[selectedPrefectureIndex]}
              setValue={(val: string) => {
                setSelectedPrefectureIndex(prefectures.indexOf(val));
              }}
            />
            <Box display="flex" flexDirection="row">
              {checkboxElements}
            </Box>
          </Box>
        </Surround>
      </Box>
    );
  };

  useEffect(() => {
    if (open) {
      // 開いたときにバックアップを作成
      setBackup(initValue());
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => handleClose()} PaperProps={screenType === "企業一覧" ? {sx: {width: 800, maxWidth: 800}} : {}}>
      <Box p={2}>
        <DialogTitle>絞り込み検索</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          {(screenType === "本部依頼一覧" || screenType === "本部履歴一覧" || screenType === "本部作成" || screenType === "店舗作成" || screenType === "企業作成") &&
            searchHeadElementRow()}
          {searchElementRow()}
          {(screenType !== "店舗作成" && screenType !== "企業作成" && screenType !== "本部作成") && searchDateElementRow()}
          <Box marginBottom={2} />
          {checkHasClassScreenType() && (
            <>
              {classification === "タイヤ" && (
                <Surround title="タイヤ用">
                  <Box marginTop={0.5} />
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="tyre-width"
                      label="幅"
                      optionList={tyreOptions.tyreWidthList.map((t) => `${t}`)}
                      value={`${tyreWidth ?? ""}`}
                      setValue={(val) => {
                        setTyreWidth(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="tyre-ratio"
                      label="偏平率"
                      optionList={tyreOptions.tyreRatioList.map((t) => `${t}`)}
                      value={`${tyreRatio ?? ""}`}
                      setValue={(val) => {
                        setTyreRatio(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="tyre-rim-diam"
                      label="タイヤリム径"
                      optionList={tyreOptions.tyreRimDiamList.map((t) => `${t}`)}
                      value={`${tyreRimDiam ?? ""}`}
                      setValue={(val) => {
                        setTyreRimDiam(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                </Surround>
              )}
              {classification === "ホイール" && (
                <Surround title="ホイール用">
                  <Box marginTop={0.5} />
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="wheel-rim-diam"
                      label="ホイールリム径"
                      optionList={wheelOptions.wheelRimDiamList.map((t) => `${t}`)}
                      value={`${wheelRimDiam ?? ""}`}
                      setValue={(val) => {
                        setWheelRimDiam(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="wheel-width"
                      label="リム幅"
                      optionList={wheelOptions.wheelWidthList.map((t) => `${t}`)}
                      value={`${wheelWidth ?? ""}`}
                      setValue={(val) => {
                        setWheelWidth(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                  <Box sx={styles.searchRowBox}>
                    <SelectForm
                      id="wheel-hole"
                      label="ホール数"
                      optionList={wheelOptions.wheelHoleList.map((t) => `${t}`)}
                      value={`${wheelHole ?? ""}`}
                      setValue={(val) => {
                        setWheelHole(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                    <SelectForm
                      id="wheel-pcd"
                      label="PCD"
                      optionList={wheelOptions.wheelPcdList.map((t) => `${t.toFixed(2)}`)}
                      value={`${wheelPcd ? wheelPcd.toFixed(2) : ""}`}
                      setValue={(val) => {
                        setWheelPcd(val === "" ? undefined : Number(val));
                      }}
                      isContainBlank
                    />
                  </Box>
                </Surround>
              )}
            </>
          )}
          {screenType === "企業一覧" && user?.identificationName === "YH_COMPANY" && // CLIENT_COMPANYはshopCdがnullなので選択できない。
            searchCheckboxElement()
          }
        </DialogContent>
        <DialogActions>
          <Button variant="custom-outlined" onClick={() => handleClose()}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // 複数検索することがあるため、大分類は必須ではない
              // if (checkHasClassScreenType() && classification.length === 0) {
              //   showAlert("大分類を設定して下さい。", "error");
              //   return;
              // }
              onSearch();
            }}
          >
            検索
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
